import React from "react";
import { AssociationProfileProps } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";

export const AssociationProfileComponent: React.FC<AssociationProfileProps> = ({
  profile,
  committee,
  onEdit,
}) => {
  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Association Profile
          </h3>
          {onEdit && (
            <button
              onClick={onEdit}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Edit
            </button>
          )}
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Basic Information
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500">Name</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {profile.name}
                  </dd>
                </div>
                {profile.purpose && (
                  <div>
                    <dt className="text-xs text-gray-500">Purpose</dt>
                    <dd className="text-sm text-gray-900">{profile.purpose}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-gray-500">Status</dt>
                  <dd>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                        profile.status === "registered"
                          ? "bg-green-100 text-green-800"
                          : profile.status === "deregistered"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {profile.status}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            {/* Registration Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Registration
              </h4>
              <dl className="space-y-3">
                {profile.registration.abn && (
                  <div>
                    <dt className="text-xs text-gray-500">ABN</dt>
                    <dd className="text-sm font-mono text-gray-900">
                      {profile.registration.abn}
                    </dd>
                  </div>
                )}
                {profile.registration.incorporationNumber && (
                  <div>
                    <dt className="text-xs text-gray-500">
                      Incorporation Number
                    </dt>
                    <dd className="text-sm font-mono text-gray-900">
                      {profile.registration.incorporationNumber}
                    </dd>
                  </div>
                )}
                {profile.registration.registrationState && (
                  <div>
                    <dt className="text-xs text-gray-500">Registered In</dt>
                    <dd className="text-sm text-gray-900">
                      {profile.registration.registrationState}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-gray-500">Registration Date</dt>
                  <dd className="text-sm text-gray-900">
                    {formatDateShort(profile.registration.registrationDate)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Financial Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Financial Year
              </h4>
              <dl className="space-y-3">
                {profile.financialYear && (
                  <div>
                    <dt className="text-xs text-gray-500">
                      Financial Year End
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {profile.financialYear}
                    </dd>
                  </div>
                )}
                {profile.rules && (
                  <div>
                    <dt className="text-xs text-gray-500">Governing Rules</dt>
                    <dd className="text-sm text-gray-900">{profile.rules}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Registered Address */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Registered Address
            </h4>
            <address className="text-sm text-gray-600 not-italic">
              {profile.registeredAddress.line1}
              <br />
              {profile.registeredAddress.line2 && (
                <>
                  {profile.registeredAddress.line2}
                  <br />
                </>
              )}
              {profile.registeredAddress.suburb},{" "}
              {profile.registeredAddress.state}{" "}
              {profile.registeredAddress.postcode}
              <br />
              {profile.registeredAddress.country}
            </address>
          </div>

          {/* Contact Details */}
          {profile.contactDetails && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Contact Details
              </h4>
              <div className="flex flex-wrap gap-6 text-sm">
                {profile.contactDetails.email && (
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <a
                      href={`mailto:${profile.contactDetails.email}`}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {profile.contactDetails.email}
                    </a>
                  </div>
                )}
                {profile.contactDetails.phone && (
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <a
                      href={`tel:${profile.contactDetails.phone}`}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {profile.contactDetails.phone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Committee Information */}
      {committee && committee.members.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Committee Members
              </h3>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded">
                {committee.members.length} members
              </span>
            </div>
          </div>

          <div className="p-6">
            {/* Positions */}
            {committee.positions.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Office Bearers
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {committee.positions.map((pos, index) => {
                    const person = committee.members.find(
                      (m) => m.id === pos.personId,
                    );
                    return (
                      <div
                        key={index}
                        className="p-3 bg-blue-50 border border-blue-200 rounded-lg"
                      >
                        <div className="text-xs text-blue-600 font-semibold uppercase">
                          {pos.position}
                        </div>
                        <div className="text-sm font-medium text-gray-900 mt-1">
                          {person
                            ? `${person.firstName} ${person.lastName}`
                            : "Unknown"}
                        </div>
                        {pos.appointmentDate && (
                          <div className="text-xs text-gray-500 mt-1">
                            Since {formatDateShort(pos.appointmentDate)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* All Members */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                All Committee Members
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {committee.members.map((member) => {
                  const positions = committee.positions
                    .filter((p) => p.personId === member.id)
                    .map((p) => p.position);

                  return (
                    <div
                      key={member.id}
                      className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="font-semibold text-gray-600">
                            {member.firstName[0]}
                            {member.lastName[0]}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                          </div>
                          {positions.length > 0 && (
                            <div className="text-xs text-blue-600">
                              {positions.join(", ")}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            member.verificationStatus === "verified"
                              ? "bg-green-100 text-green-800"
                              : member.verificationStatus === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {member.verificationStatus.replace("-", " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {member.appointmentDate && (
                          <span className="text-xs text-gray-500">
                            {formatDateShort(member.appointmentDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssociationProfileComponent;
